import {
  type Instance,
  applySnapshot,
  cast,
  flow,
  types,
} from 'mobx-state-tree';

import { getAbTests, googleAnalyticsEvent } from '@features/Analytics';

import { AbTestModel, type AbTestSnapshot } from '../ab-test/ab-test';

export const AnalyticsStoreModel = types
  .model('AnalyticsStore')
  .props({
    segment: types.maybe(types.string),
    abTests: types.maybe(types.array(AbTestModel)),
    fbPixelId: types.maybe(types.array(types.string)),
    isFetching: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    saveAbTest: (
      segmentName: string | undefined,
      tests: Array<AbTestSnapshot> | undefined,
    ) => {
      self.segment = segmentName;
      self.abTests = cast(tests);
    },
    setFbPixelId: (_fbPixelId: Array<string> | undefined) => {
      self.fbPixelId = cast(_fbPixelId);
    },
    setFetchingStatus: (status: boolean) => {
      self.isFetching = status;
    },
  }))
  .actions((self) => ({
    getAbTests: flow(function* ({
      variant,
      utm_source,
      utm_campaign,
      region,
      user_id,
      abortSignal,
    }: {
      variant: string | undefined;
      utm_source: string | undefined;
      utm_campaign: string | undefined;
      region: string | undefined;
      user_id: string | undefined;
      abortSignal?: AbortSignal;
    }) {
      self.setFetchingStatus(true);
      try {
        const responseObject = yield getAbTests({
          variant,
          utm_source,
          utm_campaign,
          region,
          user_id,
          abortSignal,
        });
        if (Object.keys(responseObject).length === 0) {
          self.saveAbTest('default', undefined);
          googleAnalyticsEvent({
            eventName: 'segment_name',
            properties: { name: 'default' },
          });
        } else {
          self.saveAbTest(responseObject.segment_name, responseObject.tests);
          googleAnalyticsEvent({
            eventName: 'segment_name',
            properties: { name: String(responseObject.segment_name) },
          });
          for (const test of responseObject.tests) {
            googleAnalyticsEvent({
              eventName: 'ab_test',
              properties: {
                name: String(test.test_name),
                group: String(test.case_name),
              },
            });
          }
        }
      } catch (error) {
        self.saveAbTest('error', undefined);
        googleAnalyticsEvent({
          eventName: 'segment_name',
          properties: { name: 'errorSegment' },
        });
        console.error(error);
      } finally {
        self.setFetchingStatus(false);
      }
    }),
  }))
  .views((self) => ({
    getCaseNameByTestName: (testName: string) =>
      self.abTests?.find((test) => test.test_name === testName)?.case_name,
  }))
  .actions((self) => ({
    reset: () => {
      applySnapshot(self, {});
    },
  }));

type AnalyticsStoreType = Instance<typeof AnalyticsStoreModel>;

export interface AnalyticsStore extends AnalyticsStoreType {}
